import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAnalytics } from '../context/AnalyticsContext';

const { FiArrowRight, FiSearch, FiShoppingCart, FiCheck, FiPhone, FiCalendar, FiTruck } = FiIcons;

const Parts = () => {
  const { trackEvent } = useAnalytics();
  const [searchQuery, setSearchQuery] = useState('');
  const [showPartForm, setShowPartForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicle: '',
    partNumber: '',
    partDescription: '',
    notes: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    trackEvent('form_fill', { type: 'parts_order' });
    setFormSubmitted(true);
    
    // In a real implementation, you'd send the form data to a server
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        vehicle: '',
        partNumber: '',
        partDescription: '',
        notes: ''
      });
      setFormSubmitted(false);
      setShowPartForm(false);
    }, 3000);
  };

  const featuredCategories = [
    {
      name: 'Wheels & Tires',
      image: 'https://images.unsplash.com/photo-1611921059084-64cca8cb9289?w=600',
      description: 'Enhance performance and style with Audi Genuine wheels and tires'
    },
    {
      name: 'Exterior Accessories',
      image: 'https://images.unsplash.com/photo-1520340356584-f9917d1eea6f?w=600',
      description: 'Premium roof racks, splash guards, and styling elements'
    },
    {
      name: 'Interior Accessories',
      image: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600',
      description: 'Floor mats, cargo solutions, and interior styling elements'
    },
    {
      name: 'Performance Parts',
      image: 'https://images.unsplash.com/photo-1621677395907-b80ea8f18177?w=600',
      description: 'Exhaust systems, air intakes, and Audi Sport enhancements'
    }
  ];

  const featuredParts = [
    {
      id: 1,
      name: 'Audi All-Weather Floor Mats',
      price: 189.99,
      image: 'https://images.unsplash.com/photo-1621089253554-25040a01bb68?w=600',
      partNumber: 'A4-FL-2022',
      compatibility: ['A4 2019-2024', 'A5 2019-2024']
    },
    {
      id: 2,
      name: 'Audi Roof Rack Base Carrier',
      price: 449.99,
      image: 'https://images.unsplash.com/photo-1551522435-a13afa10f103?w=600',
      partNumber: 'Q5-RR-2023',
      compatibility: ['Q5 2018-2024', 'SQ5 2018-2024']
    },
    {
      id: 3,
      name: 'Audi Valve Stem Caps with Logo',
      price: 29.99,
      image: 'https://images.unsplash.com/photo-1585598007006-a6b976df6090?w=600',
      partNumber: 'AU-VSC-2023',
      compatibility: ['All Audi Models']
    },
    {
      id: 4,
      name: 'Audi Cargo Box - Black',
      price: 899.99,
      image: 'https://images.unsplash.com/photo-1522918448933-7e6e2aed74fa?w=600',
      partNumber: 'AU-CARGO-BLK',
      compatibility: ['Compatible with Audi roof rack systems']
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-black text-white">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1563223771-5fe4038fbfc9?w=1920&h=600&fit=crop&crop=center" 
            alt="Audi parts and accessories" 
            className="w-full h-full object-cover opacity-60" 
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-light mb-6 uppercase tracking-tight">
              Genuine Audi Parts & Accessories
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto font-light">
              Enhance your Audi with premium parts and accessories specifically engineered for your vehicle.
            </p>
            <div className="mt-8 flex justify-center">
              <div className="flex w-full max-w-xl">
                <input
                  type="text"
                  placeholder="Search parts by name, part number, or vehicle..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-3 focus:outline-none text-gray-900"
                />
                <button className="bg-red-600 px-6 flex items-center justify-center hover:bg-red-700 transition-colors">
                  <SafeIcon icon={FiSearch} className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="bg-white py-6 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setShowPartForm(true)}
              className="bg-red-600 text-white px-6 py-3 hover:bg-red-700 transition-colors uppercase tracking-wider text-sm font-medium flex items-center space-x-2"
            >
              <SafeIcon icon={FiShoppingCart} className="w-5 h-5" />
              <span>Order Parts</span>
            </button>
            <Link 
              to="/services" 
              className="bg-black text-white px-6 py-3 hover:bg-gray-900 transition-colors uppercase tracking-wider text-sm font-medium flex items-center space-x-2"
            >
              <SafeIcon icon={FiCalendar} className="w-5 h-5" />
              <span>Schedule Installation</span>
            </Link>
            <a
              href="tel:+19024557890"
              className="border-2 border-black text-black px-6 py-3 hover:bg-black hover:text-white transition-colors uppercase tracking-wider text-sm font-medium flex items-center space-x-2"
            >
              <SafeIcon icon={FiPhone} className="w-5 h-5" />
              <span>Contact Parts Department</span>
            </a>
          </div>
        </div>
      </section>

      {/* Parts Categories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Shop By Category
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Browse our extensive selection of genuine Audi parts and accessories designed specifically for your vehicle.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredCategories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="relative h-48">
                  <img 
                    src={category.image} 
                    alt={category.name} 
                    className="w-full h-full object-cover" 
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                    {category.name}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {category.description}
                  </p>
                  <Link
                    to={`/parts/category/${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-red-600 hover:text-red-700 transition-colors inline-flex items-center space-x-1 uppercase tracking-wider text-sm"
                  >
                    <span>Explore {category.name}</span>
                    <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Featured Products
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Popular accessories and parts for your Audi, engineered for perfect fit and performance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredParts.map((part, index) => (
              <motion.div
                key={part.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="relative h-48">
                  <img 
                    src={part.image} 
                    alt={part.name} 
                    className="w-full h-full object-cover" 
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    {part.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-2">Part #: {part.partNumber}</p>
                  <p className="text-red-600 font-bold text-lg mb-2">${part.price}</p>
                  <div className="mb-4">
                    <p className="text-sm text-gray-600">Fits:</p>
                    <ul className="text-xs text-gray-500">
                      {part.compatibility.map((model, idx) => (
                        <li key={idx}>{model}</li>
                      ))}
                    </ul>
                  </div>
                  <button
                    onClick={() => {
                      setShowPartForm(true);
                      setFormData(prev => ({
                        ...prev,
                        partNumber: part.partNumber,
                        partDescription: part.name
                      }));
                      trackEvent('button_click', { type: 'order_specific_part', part: part.name });
                    }}
                    className="w-full bg-black text-white py-2 px-4 font-medium hover:bg-gray-900 transition-colors text-center uppercase tracking-wider text-sm flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiShoppingCart} className="w-4 h-4" />
                    <span>Order Now</span>
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/parts/all"
              className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm"
            >
              <span>View All Parts & Accessories</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Genuine Parts */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6 uppercase tracking-tight">
                Why Choose Genuine Audi Parts
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Genuine Audi parts are designed specifically for your vehicle, ensuring perfect fit, 
                performance, and longevity. Our parts meet the same exacting standards used in 
                manufacturing your Audi, providing peace of mind and maintaining your vehicle's value.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="w-6 h-6 text-red-600 mt-1" />
                  <div>
                    <h3 className="font-medium text-lg">Perfect Fit</h3>
                    <p className="text-gray-600">Engineered specifically for your Audi model</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="w-6 h-6 text-red-600 mt-1" />
                  <div>
                    <h3 className="font-medium text-lg">Superior Quality</h3>
                    <p className="text-gray-600">Meet or exceed original equipment specifications</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="w-6 h-6 text-red-600 mt-1" />
                  <div>
                    <h3 className="font-medium text-lg">Warranty Protection</h3>
                    <p className="text-gray-600">Backed by Audi's parts warranty</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="w-6 h-6 text-red-600 mt-1" />
                  <div>
                    <h3 className="font-medium text-lg">Value Retention</h3>
                    <p className="text-gray-600">Helps maintain your Audi's value over time</p>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://images.unsplash.com/photo-1600705722908-bbc52fb66f0c?w=800" 
                alt="Genuine Audi parts" 
                className="rounded-lg shadow-xl" 
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Installation Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Professional Installation Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Trust our factory-trained technicians to properly install your genuine Audi parts and accessories.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Expert Installation',
                description: 'Our Audi-certified technicians have the training and specialized tools to ensure proper installation of all parts and accessories.',
                icon: FiTool
              },
              {
                title: 'Installation Warranty',
                description: 'All parts installed by Halifax Audi come with a warranty on both the part and the labor performed.',
                icon: FiShield
              },
              {
                title: 'Convenient Scheduling',
                description: 'Book your installation appointment online or by phone. We offer flexible scheduling to fit your busy lifestyle.',
                icon: FiCalendar
              }
            ].map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 p-6 border border-gray-200"
              >
                <div className="bg-red-600 p-3 rounded-full w-fit mb-4">
                  <SafeIcon icon={service.icon} className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                  {service.title}
                </h3>
                <p className="text-gray-600">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/services"
              className="bg-black text-white px-8 py-3 font-medium hover:bg-gray-900 transition-colors duration-200 inline-flex items-center space-x-2 uppercase tracking-wider text-sm"
            >
              <span>Schedule Installation</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Parts Ordering Information */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-8 border border-gray-200"
            >
              <h3 className="text-2xl font-light text-gray-900 mb-6 uppercase tracking-tight">
                Parts Department Information
              </h3>
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Hours</h4>
                  <div className="space-y-1 text-gray-600">
                    <p>Monday - Friday: 8:00 AM - 6:00 PM</p>
                    <p>Saturday: 9:00 AM - 4:00 PM</p>
                    <p>Sunday: Closed</p>
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Contact</h4>
                  <div className="space-y-1">
                    <p className="text-gray-600">
                      <span className="font-medium text-gray-900">Phone:</span> (902) 455-7890 ext. 3
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium text-gray-900">Email:</span> parts@halifaxaudi.ca
                    </p>
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Address</h4>
                  <p className="text-gray-600">
                    6077 Columbus St<br />
                    Halifax, NS B3K 0E9
                  </p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-8 border border-gray-200"
            >
              <h3 className="text-2xl font-light text-gray-900 mb-6 uppercase tracking-tight">
                Ordering Information
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-red-600 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900">Online Ordering</h4>
                    <p className="text-gray-600">
                      Order parts and accessories online using our convenient order form.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-red-600 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900">Phone Orders</h4>
                    <p className="text-gray-600">
                      Call our parts specialists directly to place your order or inquire about availability.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-red-600 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900">Special Orders</h4>
                    <p className="text-gray-600">
                      We can source rare or specialized parts not typically kept in inventory.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiTruck} className="w-5 h-5 text-red-600 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900">Shipping Options</h4>
                    <p className="text-gray-600">
                      Parts can be shipped directly to your home or held for pickup at our dealership.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowPartForm(true);
                    trackEvent('button_click', { type: 'order_parts_form' });
                  }}
                  className="w-full bg-red-600 text-white py-3 px-6 font-medium hover:bg-red-700 transition-colors duration-200 uppercase tracking-wider text-sm flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiShoppingCart} className="w-5 h-5" />
                  <span>Order Parts Now</span>
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-light mb-4 uppercase tracking-tight">
              Need Help Finding the Right Part?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto font-light">
              Our Audi parts specialists can help you find exactly what you need for your vehicle.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+19024557890"
                className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 uppercase tracking-wider text-sm"
              >
                Call Parts Department
              </a>
              <button
                onClick={() => {
                  setShowPartForm(true);
                  trackEvent('button_click', { type: 'parts_inquiry_form' });
                }}
                className="border-2 border-white text-white px-8 py-3 font-medium hover:bg-white hover:text-black transition-colors duration-200 uppercase tracking-wider text-sm"
              >
                Submit Parts Inquiry
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Parts Order Form Modal */}
      {showPartForm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            {formSubmitted ? (
              <div className="text-center py-6">
                <div className="bg-green-100 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiCheck} className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Parts Request Submitted
                </h3>
                <p className="text-gray-600 mb-4">
                  Thank you for your parts request. A member of our parts department will contact you shortly to confirm availability and pricing.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                  Order Parts & Accessories
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name*
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address*
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number*
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vehicle Information*
                    </label>
                    <input
                      type="text"
                      name="vehicle"
                      value={formData.vehicle}
                      onChange={handleInputChange}
                      placeholder="Year, Model (e.g., 2022 Audi Q5)"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Part Number (if known)
                    </label>
                    <input
                      type="text"
                      name="partNumber"
                      value={formData.partNumber}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Part Description*
                    </label>
                    <input
                      type="text"
                      name="partDescription"
                      value={formData.partDescription}
                      onChange={handleInputChange}
                      placeholder="e.g., Floor Mats, Brake Pads, Oil Filter"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Notes
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Please provide any additional details about your parts request"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    ></textarea>
                  </div>
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-red-700 transition-colors"
                    >
                      Submit Request
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowPartForm(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Parts;